import { useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { useWaReady } from "@/components/WhatsAppButton";
import { waLinkWithText } from "@/lib/whatsapp";

const ago = (iso) => {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d}d ago`;
};

/**
 * One-click payment reminder for a closed case (kind="case") or all of a client's closed
 * unpaid cases (kind="client"). WhatsApp opens a pre-written chat; email is sent from the
 * firm's mailbox when connected, otherwise the staff member's email app opens with the draft.
 * Every reminder is logged on the case(s) and the client's interaction history.
 */
export function FeeReminderButtons({ kind, id, email, phone, last, onDone, testid }) {
  useWaReady();
  const [busy, setBusy] = useState("");
  const url = kind === "client" ? `/clients/${id}/fee-reminder` : `/applications/${id}/fee-reminder`;
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const remind = async (channel, e) => {
    stop(e);
    if (busy) return;
    // open the WhatsApp tab synchronously so the browser doesn't block it as a popup
    const win = channel === "whatsapp" ? window.open("", "_blank") : null;
    setBusy(channel);
    try {
      const { data } = await api.post(url, { channel });
      if (channel === "whatsapp") {
        const link = waLinkWithText(data.phone, data.text);
        if (win) win.location.href = link; else window.open(link, "_blank", "noopener,noreferrer");
        toast.success("WhatsApp reminder opened and logged");
      } else if (data.mode === "sent") {
        toast.success(`Reminder emailed to ${data.to}`);
      } else {
        window.location.href = `mailto:${data.to}?subject=${encodeURIComponent(data.subject)}&body=${encodeURIComponent(data.text)}`;
        toast.success("Reminder drafted in your email app and logged");
      }
      onDone?.(data.reminder);
    } catch (err) {
      win?.close();
      toast.error(apiErr(err));
    } finally { setBusy(""); }
  };

  const btn = "inline-flex items-center gap-1 h-7 px-2 rounded-lg border text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  return (
    <div className="flex flex-col items-end gap-0.5" data-testid={testid} onClick={stop}>
      <div className="flex items-center gap-1">
        <button type="button" data-testid={`${testid}-whatsapp`} disabled={!phone || !!busy} onClick={(e) => remind("whatsapp", e)}
          title={phone ? "Send a payment reminder on WhatsApp" : "No phone number on file"}
          className={`${btn} border-[#25D366]/40 text-[#128C7E] bg-[#25D366]/5 hover:bg-[#25D366]/15`}>
          {busy === "whatsapp" ? <Loader2 className="h-3 w-3 animate-spin" /> : <WhatsAppIcon className="h-3 w-3" />} Remind
        </button>
        <button type="button" data-testid={`${testid}-email`} disabled={!email || !!busy} onClick={(e) => remind("email", e)}
          title={email ? "Email a payment reminder" : "No email on file"}
          className={`${btn} border-slate-200 text-slate-600 bg-white hover:border-primary/40 hover:text-primary`}>
          {busy === "email" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Remind
        </button>
      </div>
      {last?.at && <span data-testid={`${testid}-last`} className="text-[10px] text-slate-400 whitespace-nowrap">Reminded {ago(last.at)} · {last.channel === "email" ? "Email" : "WhatsApp"}</span>}
    </div>
  );
}
